/**
 * Todos los textos visibles, en un solo lugar.
 *
 * La convención del repo prohíbe cadenas literales en el JSX. Aquí además paga
 * doble: la herramienta la usan LALA y Dive.AI, y tener el vocabulario
 * centralizado evita que "fuente atrasada" y "entrega tardía" convivan en dos
 * pantallas distintas.
 */

export const APP = {
  title: 'Seguimiento Operativo',
  subtitle: 'LALA · Pricing',
}

export const NAV = {
  today: 'Hoy',
  calendar: 'Calendario',
  refresh: 'Ciclos',
  backlog: 'Roadmap',
  roadmap: 'Plan',
  rgm: 'Backlog RGM',
}

export const SOURCE_STATUS: Record<string, string> = {
  upcoming: 'Próxima',
  expected_today: 'Vence hoy',
  available: 'Disponible',
  delayed: 'Atrasada',
  validation_pending: 'En validación',
  validated: 'Validada',
  action_required: 'Requiere acción',
  unconfigured: 'Sin ruta',
  check_failed: 'No verificable',
  not_applicable: 'Sin fecha',
  baseline: 'Al corriente (línea base)',
}

export const STAGE_STATUS: Record<string, string> = {
  not_started: 'Sin iniciar',
  in_progress: 'En curso',
  done: 'Terminada',
  late: 'Atrasada',
  at_risk: 'En riesgo',
  blocked: 'Bloqueada',
}

export const RISK: Record<string, string> = {
  on_track: 'En tiempo',
  at_risk: 'Consumiendo buffer',
  breached: 'Deadline en riesgo',
  done: 'Cerrado en tiempo',
}

export const SEVERITY: Record<string, string> = {
  info: 'Informativa',
  warning: 'Atención',
  critical: 'Crítica',
}

export const TODAY_VIEW = {
  /**
   * El titular del tablero, partido en tres para poder componerlo: la cifra va
   * en acento, el cuerpo en redonda y el cierre en cursiva. Se conjuga porque
   * una herramienta que dice "1 cosas requieren" se siente rota, aunque el dato
   * esté bien.
   */
  hero: (n: number) =>
    n === 0
      ? { count: null, body: 'Todo va ', tail: 'en tiempo.' }
      : {
          count: String(n),
          body: n === 1 ? ' cosa requiere tu ' : ' cosas requieren tu ',
          tail: 'atención hoy.',
        },
  heroCalm: 'Ninguna fuente está atrasada y ningún ciclo está consumiendo su buffer.',
  statDelayed: (n: number) => (n === 1 ? '1 fuente atrasada' : `${n} fuentes atrasadas`),
  statAtRisk: (n: number) => (n === 1 ? '1 ciclo en riesgo' : `${n} ciclos en riesgo`),
  statBuffer: 'buffer',
  statChecked: 'revisado',
  statNeverChecked: 'sin revisar',
  needsAttention: 'Requiere tu atención',
  nothingToDo: 'Nada requiere acción hoy.',
  nothingToDoHint: 'Las fuentes van en tiempo y ningún ciclo está en riesgo.',
  freshness: 'Con qué datos está cada cosa',
  freshnessHint: 'Hasta qué mes de datos llega cada producto, según los ciclos cerrados.',
  freshnessUpTo: 'Al día hasta',
  freshnessNever: 'Sin cierres registrados',
  freshnessInProgress: 'en curso',
  freshnessNotStarted: 'sin empezar',
  nextMilestones: 'Próximos hitos',
  activeCycles: 'Ciclos en curso',
  impact: 'Impacto',
  action: 'Acción',
  responsible: 'Responsable',
  noMilestones: 'Sin hitos en el horizonte.',
}

export const CALENDAR_VIEW = {
  title: 'Calendario maestro',
  hint: 'Una línea por fuente. Cada punto es un mes de datos; el color, su estado.',
  hintTable: 'Fuente por mes de datos. La celda muestra la fecha comprometida; el color, el estado.',
  modeText: 'Fichas',
  modeLanes: 'Líneas',
  modeMonth: 'Mes',
  modeTable: 'Tabla',
  hintMonth: 'El mes día por día: entregas comprometidas, ventanas de ciclo y sprints.',
  hintText: 'Una ficha por fuente: en qué producto está, qué hay en SharePoint y si vamos tarde.',
  textInProduct: {
    herramienta: 'En herramienta',
    dashboard: 'En dashboard',
    escenarios: 'En escenarios',
  } as Record<string, string>,
  textSharepoint: 'Disponible en SharePoint',
  textDeadline: 'Deadline siguiente superada',
  textYes: 'SÍ',
  textNo: 'NO',
  textNoProducts: 'No alimenta ningún ciclo de actualización.',
  textNothing: 'Nada detectado todavía',
  textLastChecked: 'Última revisión',
  textNeverChecked: 'sin revisar',
  textOverdueSince: (label: string, dias: number) =>
    `${label} — ${dias} ${dias === 1 ? 'día' : 'días'} de retraso`,
  deliveries: 'Entregas de datos',
  cycles: 'Ciclos',
  sprints: 'Sprints',
  emptyMonth: 'Nada programado en este mes.',
  prevMonth: 'Mes anterior',
  nextMonth: 'Mes siguiente',
  arrived: 'Llegó',
  pending: 'Pendiente',
  weekend: 'Fin de semana',
  deadlineMark: 'Deadline',
  sprintProgress: 'Avance',
  legendArrived: 'Llegó',
  legendExpected: 'Comprometida',
  legendWeekend: 'Fin de semana',
  legendToday: 'Hoy',
  today: 'Hoy',
  empty: 'Ningún dato coincide con los filtros.',
  source: 'Fuente',
  expected: 'Esperada',
  actual: 'Real',
  owner: 'Data owner',
  custodian: 'Custodian',
  steward: 'Steward',
  monthsBack: 'Meses atrás',
  monthsForward: 'Meses adelante',
  lastChecked: 'Última revisión',
  blocking: 'Está deteniendo el arranque del ciclo',
  noPath: 'Sin ruta de SharePoint configurada: no se puede verificar solo.',
}

export const REFRESH_VIEW = {
  title: 'Ventana de refresh',
  window: 'Ventana',
  deadline: 'Deadline',
  buffer: 'Buffer',
  bufferRemaining: 'Buffer restante',
  progress: 'Avance',
  stages: 'Etapas',
  latestStart: 'Último inicio',
  latestFinish: 'Último fin',
  projected: 'Proyectado',
  slack: 'Holgura',
  blockedBy: 'Falta por llegar',
  readyToStart: 'Todas las fuentes están disponibles',
  computeWarning: 'Corridas largas',
  criticalPath: 'Ruta crítica',
  days: 'días',
  overrideNote: 'Corrida con fechas ajustadas',
  overDeadline: 'Sobre el deadline',
}

// Reporte de refresh (BL-050): la tabla por tabla contra la versión anterior y
// el tiempo por etapa, generado por build_refresh_report.py.
export const REFRESH_REPORT = {
  title: 'Reporte del refresh',
  none: 'Todavía no se ha subido el reporte de esta corrida.',
  howTo:
    'Lo genera el repo de pricing: run_post_refresh_reports.sh (etapa 0) lo deja en reports/post_refresh/refresh_report/<fecha>/refresh_report.html. Súbelo tal cual.',
  upload: 'Subir reporte',
  uploading: 'Subiendo…',
  uploaded: 'Reporte subido',
  open: 'Abrir reporte',
  opening: 'Abriendo…',
  alerts: 'Alertas',
  alertsHint: 'Cambió en este refresh',
  standing: 'Condiciones vigentes',
  standingHint: 'Ya estaban en la versión anterior',
  tables: 'Tablas comparadas',
  runtime: 'Tiempo limpio',
  runtimeHint: 'Última duración exitosa de cada fase',
  topStages: 'Etapas más costosas',
  noAlerts: 'Sin alertas: nada cambió más allá de la tolerancia.',
  moreAlerts: 'más en el reporte',
  versions: 'Versiones',
  gate: 'Gate',
  uploadedBy: 'Subido por',
  previous: 'Anteriores',
}

export const STAGE_EDITOR = {
  open: 'Registrar avance',
  title: 'Registrar avance',
  startedOn: 'Inicio',
  finishedOn: 'Fin',
  note: 'Nota',
  notePlaceholder: 'Qué pasó, o de dónde sale la fecha',
  finishToday: 'Terminada hoy',
  startToday: 'Iniciada hoy',
  save: 'Guardar',
  clear: 'Limpiar',
  saving: 'Guardando…',
  saved: 'Avance registrado',
  cleared: 'Etapa sin registrar',
  // El backend interpreta un campo vacío como "no lo cambies", nunca como
  // "bórralo". Sin decirlo, vaciar el campo parece un borrado que no ocurre.
  emptyKeeps: 'Un campo vacío deja el valor como está. Usa Limpiar para borrar ambos.',
  finishBeforeStart: 'El fin no puede ser antes del inicio.',
  nothingToSave: 'Pon al menos una fecha.',
}

export const FILTERS = {
  title: 'Filtros',
  owner: 'Responsable',
  sprint: 'Sprint',
  epic: 'Epic',
  status: 'Estado',
  source: 'Fuente',
  cycle: 'Ciclo',
  period: 'Mes de datos',
  all: 'Todos',
  clear: 'Limpiar',
  mine: 'Sólo lo mío',
}

export const AUTH = {
  signIn: 'Iniciar sesión',
  signInHint: 'Este panel es privado. Inicia sesión para continuar.',
  signOut: 'Cerrar sesión',
  unauthorized: 'Tu cuenta no tiene acceso a este panel.',
  unauthorizedHint: 'Solicita acceso al equipo de Dive.AI.',
}

export const REFRESH_BUTTON = {
  label: 'Actualizar',
  running: 'Consultando…',
  tooltip: 'Consulta SharePoint ahora y actualiza el tablero',
  lastChecked: 'Última revisión',
  never: 'Nunca se ha consultado SharePoint',
  done: 'Listo',
  found: 'encontradas',
  missing: 'faltantes',
  errors: 'con error',
  failed: 'No se pudo consultar SharePoint',
  inProgress: 'Consultando SharePoint…',
  alreadyRunning: 'Ya hay una consulta en curso',
  checked: 'revisadas',
}

export const SOURCE_STATUS_EXTRA = {
  baseline: 'Cubierta por línea base',
}

// Qué producto alimenta cada fuente. Las claves las produce el backend
// derivándolas de `requires`; aquí sólo se les pone nombre.
export const FEEDS: Record<string, string> = {
  dashboard: 'Dashboard',
  pricing_tool: 'Herramienta de pricing',
  both: 'Dashboard y herramienta de pricing',
  none: 'Fuera de los ciclos',
}

export const FEEDS_SHORT: Record<string, string> = {
  dashboard: 'Dashboard',
  pricing_tool: 'Pricing',
  both: 'Ambos',
  none: 'Fuera de ciclo',
}

export const SOURCE_DRAWER_FEEDS = 'Alimenta'

export const COMMON = {
  loading: 'Cargando…',
  error: 'No se pudo cargar la información.',
  retry: 'Reintentar',
  today: 'Hoy',
  close: 'Cerrar',
  details: 'Detalle',
  file: 'Archivo',
  none: '—',
}

// El plan guarda claves de rol; la UI muestra nombres. Una sola definición:
// estaba duplicada en App y en RoadmapView, y divergir habría dado dos nombres
// para la misma persona según la pantalla.
export const OWNER_NAME: Record<string, string> = { TL: 'Lu', DS: 'Javi', SWE: 'Mike' }

export const ROADMAP_VIEW = {
  unavailable: 'El plan de entrega no está disponible',
  unavailableHint: 'Falta config/roadmap.json — se mantiene en el repo de esta plataforma.',
  generated: 'Plan generado',
  sprintGoal: 'Objetivo',
  tasks: 'tareas',
  noTasks: 'Sin tareas en este sprint',
  unscheduled: 'Fuera del horizonte',
  unscheduledHint: 'Items sin sprint asignado. Se pueden traer al plan igual que cualquier otro.',
  move: 'Mover a',
  moveTo: 'Mover a sprint',
  remove: 'Sacar del horizonte',
  reset: 'Devolver al plan',
  moved: 'movida',
  movedFrom: (from: string | null) => (from ? `el plan la puso en ${from}` : 'no estaba en el plan'),
  committed: 'comprometido',
  budget: 'presupuesto',
  over: 'sobre presupuesto',
  refreshWindow: 'Ventana de refresh',
  holiday: 'Sprint protegido',
  depWarning: 'Aviso de dependencias',
  moveOk: 'Movida',
  moveFailed: 'No se pudo mover',
  deps: 'Depende de',
  planned: 'Planeado',
  effort: 'Esfuerzo',
  filterOwner: 'Persona',
  filterAll: 'Todas',
}

export const PLAN_DOC = {
  unavailable: 'El plan de entrega no está disponible',
  unavailableHint: 'Falta config/roadmap.json — se mantiene en el repo de esta plataforma.',
  nav: {
    overview: 'Resumen',
    rhythm: 'Ciclos',
    architecture: 'Arquitectura',
    epics: 'Épicas',
    sprints: 'Sprints',
    traceability: 'Trazabilidad',
    risks: 'Riesgos',
  },
  overviewTitle: 'Sesenta y dos items, tres personas, y un calendario ya comprometido',
  rhythmTitle: 'El calendario que el equipo ya debe antes de cualquier roadmap',
  archTitle: 'La arquitectura que dicta el orden de los sprints',
  archLede:
    'Diagramas C4 derivados del repositorio, no decorativos: cada uno existe para explicar una decisión de secuencia. Línea sólida es lo que ya existe; punteado violeta es lo que este plan propone.',
  epicsTitle: 'Diez épicas, cada una con su criterio de cierre',
  sprintsTitle: 'Diez sprints de dos semanas',
  traceTitle: 'Cada item del Excel, y dónde acabó',
  traceLede:
    'Los 62 items abiertos más los 11 que reveló el repositorio. Ninguno se eliminó en silencio: lo que no se agendó lleva su razón.',
  epicsLede:
    'Una épica es una unidad de valor; un sprint es una quincena fija. Son ejes distintos, y por eso casi todos los sprints de este plan avanzan entre tres y seis épicas a la vez: es lo que mantiene ocupados a los tres carriles en paralelo y lo que evita que una épica bloqueada cueste una quincena entera. Cada épica lleva abajo su recorrido sprint por sprint y la fecha en que cierra.',
  calendarTitle: 'Sprint → épica → calendario',
  calendarHint:
    'Cada columna es un sprint de dos semanas; cada fila, una épica. La celda se llena cuando esa épica tiene trabajo agendado en ese sprint. En la cabecera: borde rojo = sprint de alto riesgo; borde punteado = el sprint protegido de diciembre.',
  sprintBySprint: 'Sprint por sprint',
  opens: 'Abre',
  closes: 'Cierra',
  closesOn: 'cierra',
  window: 'Ventana',
  itemsDelivered: 'Items entregados',
  load: 'Carga',
  dormantIn: 'En pausa en',
  dormantHint: 'la épica se detiene ahí y retoma después; no es trabajo continuo.',
  inHorizonDays: 'días persona en horizonte',
  nothingScheduled: 'Nada agendado dentro del horizonte.',
  risksTitle: 'Qué puede descarrilar esto, y qué nadie ha contestado',
  decisions: 'Las decisiones que le dan forma al plan',
  whoDoesWhat: 'Quién hace qué',
  capacity: 'Comprometido',
  method: 'Método, y sus límites',
  figure: 'Figura',
  inHorizon: 'en horizonte',
  beyond: 'fuera',
  problem: 'Problema',
  why: 'Por qué importa',
  outcome: 'Resultado buscado',
  scope: 'Alcance',
  exclusions: 'Exclusiones',
  success: 'Criterio de cierre',
  deps: 'Dependencias',
  components: 'Componentes afectados',
  includedItems: 'Items incluidos',
  tasks: 'tareas',
  outOfHorizon: 'fuera',
  budget: 'Presupuesto del sprint',
  capacityNote: 'Nota de capacidad',
  deliverables: 'Entregables',
  risks: 'Riesgos',
  endstate: 'Estado al cerrar el sprint',
  parallel: 'Paralelo vs secuencial',
  refreshWindow: 'Ventana de refresh',
  protected: 'Protegido',
  highRisk: 'Riesgo alto',
  description: 'Qué hay que cambiar',
  approach: 'Enfoque propuesto',
  sizing: 'Por qué esta estimación',
  evidence: 'Evidencia en el repositorio',
  rationale: 'Razón del cambio de prioridad',
  acceptance: 'Criterios de aceptación',
  excelPriority: 'Excel',
  dependsOn: 'Depende de',
  item: 'Item',
  excel: 'Excel',
  recommended: 'Recomendada',
  epic: 'Épica',
  sprint: 'Sprint',
  owner: 'Responsable',
  decision: 'Decisión',
  externalDeps: 'Dependencias externas',
  openQuestions: 'Preguntas abiertas',
  blocks: 'bloquea',
}

/**
 * Backlog RGM: las hijas de "Backlog RGM" en Notion, sincronizadas a las 7 a.m.
 * y reflejadas en el Azure DevOps de LALA.
 */
export const RGM_VIEW = {
  eyebrow: 'Notion → Azure DevOps',
  title: 'Backlog RGM',
  subtitle: 'Sincronizado cada día a las 7:00. Lo que se ve aquí es lo que ve LALA en Azure DevOps.',
  syncedAt: 'Última sincronización',
  syncedValue: (day: string, hhmm: string, rel: string) => `${day} ${hhmm} UTC · ${rel}`,
  notion: 'Notion',
  neverSynced: 'Todavía no se ha sincronizado',
  lastAttemptFailed: 'El último intento falló; se muestra lo último que sí se leyó',
  syncNow: 'Sincronizar ahora',
  syncing: 'Sincronizando…',
  syncDone: 'Sincronización terminada',
  connect: 'Conectar Notion',
  reconnect: 'Reconectar Notion',
  connectedTo: (ws: string | null) => (ws ? `Conectado a Notion · ${ws}` : 'Conectado a Notion'),
  notConnected: 'Notion no está conectado. Un admin tiene que pulsar «Conectar Notion».',
  oauthMissing: 'Faltan NOTION_CLIENT_ID / NOTION_CLIENT_SECRET en el servicio.',
  ado: 'Azure DevOps',
  adoOff: (reason: string | null) => `Apagado${reason ? ` — ${reason}` : ''}`,
  adoCounts: (c: number, u: number, n: number, r: number) =>
    `${c} creados · ${u} actualizados · ${n} sin cambios · ${r} retirados`,
  adoErrors: (n: number) => (n === 1 ? '1 task rechazada' : `${n} tasks rechazadas`),
  connectedToast: (ws: string | null) =>
    `Notion conectado${ws ? ` (${ws})` : ''}. Pulsa «Sincronizar ahora».`,
  deniedToast: 'Se canceló la autorización en Notion.',
  errorToast: (d: string | null) => `No se pudo conectar Notion${d ? `: ${d}` : ''}`,
  empty: 'El Backlog RGM no tiene tasks.',
  colTask: 'Tarea',
  colStatus: 'Status',
  colOwner: 'Asignee',
  colPriority: 'Prioridad',
  colDue: 'Vence',
  openInNotion: 'Abrir en Notion',
  count: (n: number) => (n === 1 ? '1 task' : `${n} tasks`),
}

